import React, {useEffect} from 'react';
import styles from '@dazn/lp-web/src/components/BannerSlider/banner-slider.module.css';
import {
    BadgeTypes,
    IBannerSlide,
    IVideo,
    PRICE_VARIABLE
} from '@dazn/lp-web/src/types';
import Markdown from 'markdown-to-jsx';
import StaticImage from '@dazn/lp-web/src/components/Images/ImageComponent/Image';
import {useLandingPagesContext} from '@dazn/lp-web/src/contexts/LandingPageContext';
import {getButtonItemId} from '@dazn/lp-web/src/proxies';
import {triggerElementClick} from '@dazn/lp-web/src/analytics/fe-events';
import {elementClickEventTypes} from '@dazn/lp-web/src/analytics/fe-events/elementClick';
import {checkIsTravelPassEnabled} from '@dazn/lp-web/src/features/getIsTravelPassEnable';
import classNames from 'classnames';
import {useCountdown} from '@dazn/lp-web/src/hooks/useCountdown';
import {CountdownTimer} from '@dazn/lp-web/src/components/Countdown';
import VideoBanner from '@dazn/lp-web/src/components/VideoBanner/VideoBanner';
import {applyGoldColor} from '../../ContentTiers/ContentTiers';
import isQueryParamTrue from '@dazn/lp-web/src/utils/isQueryparamTrue';
import {shouldSuperImpose} from '../../Header/Utils/features';
import {getRobloxId, handleBoxingBeta} from '@dazn/lp-web/src/utils/boxingBeta';
import {externalServices} from '@dazn/lp-web/src/constants/resourceStrings.constants';
import {useVideoBannerContext} from '../../VideoBanner/VideoBannerContext';
import {bannerTypes} from '@dazn/lp-web/src/constants/bannerTypes.constants';
import {RotatingContentBanner} from './RotatingContentBanner';
import {useFirstLoad} from '@dazn/lp-web/src/hooks/useFirstLoad';

const DesktopBannerItem: React.FC<{
    data: IBannerSlide;
    banners: IBannerSlide[];
}> = ({data, banners}) => {
    const isFirstLoad = useFirstLoad();
    const backgroundImage = data?.backgroundImage;
    const isVideoBanner = data?.backgroundType === 'video';
    const videoPosterImage = data?.videoPosterImage;
    const videoUrl: IVideo = data?.videoUrl;
    const {getPromoCodeByEntSetId} = useLandingPagesContext();
    const {setThumbnail} = useVideoBannerContext();
    const promoCode = getPromoCodeByEntSetId(data?.entitlementSetId);
    const {buttonId} = externalServices.roblox;
    const timer = Boolean(data?.showCountDownTimer)
        ? useCountdown(data?.countdownTime)
        : null;

    useEffect(() => {
        setThumbnail(videoPosterImage);
    }, [isVideoBanner]);

    const isRotatingContentBanner =
        data?.bannerType?.toLowerCase() ===
        bannerTypes.ROTATING_CONTENT.toLowerCase();

    const hidePriceString =
        typeof data?.price === 'string' &&
        data?.price?.includes(PRICE_VARIABLE);

    return (
        <div className={styles.bannerSlideWrapper}>
            <div
                className={classNames(styles.banner, {
                    [styles.withImposedContentTiers]:
                        banners.length === 1 && shouldSuperImpose()
                })}
            >
                {data?.backgroundType === 'image' && (
                    <StaticImage
                        image={backgroundImage}
                        alt="Spotlight Background Image"
                        data-target="banner-image"
                    />
                )}
                {data?.backgroundType === 'video' && (
                    <>
                        <StaticImage
                            image={videoPosterImage}
                            alt="Spotlight Background Image"
                            data-target="banner-image"
                        />
                        <VideoBanner title={data?.title} url={videoUrl} />
                    </>
                )}

                <div
                    className={classNames(styles.bannerContent, {
                        [styles.bannerContentWithTravelpass]:
                            checkIsTravelPassEnabled(),
                        [styles.bannerWithRotatingContent]:
                            isRotatingContentBanner,
                        [styles.noAnimation]: isFirstLoad
                    })}
                >
                    {data?.goldenLabel && (
                        <p
                            className={classNames(
                                styles.bannerLabel,
                                styles.bannerLabelGolden
                            )}
                            data-target="banner-label"
                        >
                            {data?.goldenLabel}
                        </p>
                    )}
                    {data?.logo && (
                        <img
                            className={styles.bannerLogo}
                            src={data?.logo}
                            data-target="banner-logo"
                        />
                    )}
                    {data?.label && (
                        <p
                            className={classNames(styles.bannerLabel, {
                                [styles.bannerLabelGolden]:
                                    data?.lableType === BadgeTypes.GOLDEN,
                                [styles.bannerLabelSecondary]:
                                    data?.lableType === BadgeTypes.SECONDARY
                            })}
                            data-target="banner-label"
                        >
                            {data?.label}
                        </p>
                    )}

                    {Boolean(data?.showCountDownTimer) &&
                        data?.countdownVariant === 'V2' && (
                            <div
                                style={{paddingBlock: '4px 16px'}}
                                data-target="banner-countdown"
                            >
                                <CountdownTimer
                                    hideCountdownZero={data?.hideCountdownZero}
                                    timer={timer}
                                    units={data?.countdownUnits}
                                    variant="V2"
                                />
                            </div>
                        )}

                    {data?.description && isRotatingContentBanner && (
                        <Markdown
                            className={styles.rotatingBannerDescription}
                            data-target="banner-description"
                            options={{
                                forceBlock: true
                            }}
                        >
                            {applyGoldColor(data?.description)}
                        </Markdown>
                    )}

                    {isRotatingContentBanner && data?.title && (
                        <Markdown
                            as="p"
                            className={styles.rotatingBannerTitle}
                            data-target="banner-title"
                            options={{
                                forceBlock: true
                            }}
                        >
                            {data?.title}
                        </Markdown>
                    )}

                    {data?.title &&
                        !isRotatingContentBanner &&
                        (checkIsTravelPassEnabled() ? (
                            <Markdown
                                className={styles.bannerTitleTravelPass}
                                data-target="banner-title"
                                options={{
                                    forceBlock: true
                                }}
                            >
                                {applyGoldColor(data?.title)}
                            </Markdown>
                        ) : (
                            <Markdown
                                className={styles.bannerTitle}
                                data-target="banner-title"
                                options={{
                                    forceBlock: true
                                }}
                            >
                                {applyGoldColor(data?.title)}
                            </Markdown>
                        ))}

                    {data?.description && !isRotatingContentBanner && (
                        <Markdown
                            className={styles.bannerDescription}
                            data-target="banner-description"
                            options={{
                                forceBlock: true
                            }}
                        >
                            {applyGoldColor(data?.description)}
                        </Markdown>
                    )}
                    {isRotatingContentBanner &&
                        data?.rotatingContentItem?.length > 0 && (
                            <RotatingContentBanner
                                rotatingContentItem={
                                    data?.rotatingContentItem || []
                                }
                            />
                        )}
                    {data?.showPrice && data?.price && !hidePriceString && (
                        <p className={styles.price} data-target="banner-price">
                            {data?.price}
                        </p>
                    )}
                    {data?.offerLabel && data?.showOfferLabel && (
                        <p
                            className={styles.bannerOfferLabel}
                            data-target="banner-offer-label"
                        >
                            {data?.offerLabel}
                        </p>
                    )}
                    {Boolean(data?.showCountDownTimer) &&
                        data?.countdownVariant === 'V1' && (
                            <div
                                style={{paddingBlockEnd: '24px'}}
                                data-target="banner-countdown"
                            >
                                <CountdownTimer
                                    hideCountdownZero={data?.hideCountdownZero}
                                    timer={timer}
                                    units={data?.countdownUnits}
                                    variant="V1"
                                />
                            </div>
                        )}
                    {Boolean(data?.actions?.length) && (
                        <div
                            className={classNames(styles.bannerActions, {
                                [styles.rotatingBannerActions]:
                                    isRotatingContentBanner
                            })}
                            data-target="banner-actions"
                        >
                            {data?.actions?.map((btn, idx: number) => {
                                const isBoxingBeta = btn.buttonId === buttonId;

                                return isQueryParamTrue('seo') ? (
                                    <a
                                        key={idx}
                                        href={btn.navigationLink}
                                        className={classNames(
                                            'swiper-no-swiping',
                                            {
                                                [styles.golden]:
                                                    btn.buttonType === 'golden',
                                                [styles.primary]:
                                                    !btn.buttonType ||
                                                    btn.buttonType === 'primary'
                                            },
                                            'seoButton',
                                            styles.bannerSeoBtn
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: btn?.label ?? ''
                                        }}
                                    ></a>
                                ) : (
                                    <button
                                        key={idx}
                                        onClick={() => {
                                            const buttonItemId =
                                                getButtonItemId(btn);
                                            triggerElementClick({
                                                section:
                                                    elementClickEventTypes.BODY,
                                                itemId: buttonItemId,
                                                resourceString: btn.label
                                            });
                                            btn.action({promoCode});

                                            isBoxingBeta && handleBoxingBeta();
                                        }}
                                        data-index={idx.toString()}
                                        className={classNames(
                                            'swiper-no-swiping',
                                            {
                                                [styles.golden]:
                                                    btn.buttonType === 'golden',
                                                [styles.primary]:
                                                    !btn.buttonType ||
                                                    btn.buttonType === 'primary'
                                            }
                                        )}
                                        dangerouslySetInnerHTML={{
                                            __html: btn?.label ?? ''
                                        }}
                                        hidden={
                                            isBoxingBeta &&
                                            Boolean(getRobloxId())
                                        }
                                    ></button>
                                );
                            })}
                        </div>
                    )}
                    {data?.disclaimer && (
                        <Markdown
                            className={classNames(styles.bannerDisclaimer, {
                                [styles.rotatingBannerDisclaimer]:
                                    isRotatingContentBanner
                            })}
                            data-target="banner-disclaimer"
                            options={{
                                forceBlock: true
                            }}
                        >
                            {data?.disclaimer}
                        </Markdown>
                    )}
                </div>
            </div>
        </div>
    );
};

export {DesktopBannerItem as default};
